import React, { useEffect, useState } from "react";

// Draggable + resizable logo box overlaid on a relatively-positioned container
// (the cover/body preview). `logo` = { url, x, y, width } normalized 0..1 to the
// container. `onChange(path, value)` writes back (e.g. "logo.x"). `lockY` keeps
// the vertical position fixed (used in the thin header band).
//
// Mouse-gesture math mirrors the autopdf bbox drag/resize (useBboxGesture).

const round = (v) => Math.round(v * 1000) / 1000;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// bandMode: thin running-header band — the logo is sized to the band height
// (not the box width), vertically centred, and only draggable horizontally (its
// x is shared with the cover so branding stays aligned).
const DraggableLogo = ({ logo, onChange, containerRef, lockY = false, bandMode = false, path = "logo.cover" }) => {
  const [gesture, setGesture] = useState(null);
  const vLock = lockY || bandMode;

  const start = (kind) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setGesture({
      kind,
      mouseX: e.clientX,
      mouseY: e.clientY,
      box: { x: logo.x, y: logo.y, width: logo.width },
    });
  };

  useEffect(() => {
    if (!gesture) return;
    const onMove = (e) => {
      const r = containerRef.current?.getBoundingClientRect();
      if (!r) return;
      const dx = (e.clientX - gesture.mouseX) / r.width;
      const dy = (e.clientY - gesture.mouseY) / r.height;
      let { x, y, width } = gesture.box;
      if (gesture.kind === "move") {
        x = clamp(x + dx, 0, bandMode ? 0.9 : 1 - width);
        if (!vLock) y = clamp(y + dy, 0, 0.95);
      } else {
        width = clamp(width + dx, 0.03, 0.6);
        x = clamp(x, 0, 1 - width);
      }
      onChange(`${path}.x`, round(x));
      if (!vLock) onChange(`${path}.y`, round(y));
      if (!bandMode) onChange(`${path}.width`, round(width));
    };
    const onUp = () => setGesture(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gesture, vLock, bandMode]);

  const style = bandMode
    ? { left: `${logo.x * 100}%`, top: "50%", transform: "translateY(-50%)", height: "88%" }
    : { left: `${logo.x * 100}%`, width: `${logo.width * 100}%`, top: `${logo.y * 100}%` };

  return (
    <div
      className={`absolute group z-20 ${gesture ? "cursor-grabbing" : "cursor-move"}`}
      style={style}
      onMouseDown={start("move")}
    >
      {logo.url ? (
        <img
          src={logo.url}
          alt="logo"
          className={`${bandMode ? "h-full w-auto" : "w-full h-auto"} pointer-events-none select-none`}
          draggable={false}
        />
      ) : (
        <div className={`${bandMode ? "h-full" : "w-full"} flex items-center justify-center bg-white/80 rounded px-1 py-0.5 text-[8px] font-bold text-[#1f2937] text-center pointer-events-none whitespace-nowrap`}>
          YOUR LOGO
        </div>
      )}
      {/* Selection outline (+ resize handle on the cover). */}
      <div className={`absolute inset-0 border border-dashed border-[#BB42AE] pointer-events-none ${gesture ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
      {!bandMode && (
        <div
          onMouseDown={start("resize")}
          className={`absolute -right-1 -bottom-1 w-2.5 h-2.5 bg-[#BB42AE] rounded-sm cursor-nwse-resize ${gesture ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
        />
      )}
    </div>
  );
};

export default DraggableLogo;
